import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogHeader } from '../atoms/dialog';
import { Button } from '../atoms/button';
import { FilePlus, FileSearch, Paperclip, Trash2, X } from 'lucide-react';
import Icon from '../molecules/Icon';

export interface ProjectAttachment {
  id: string;
  fileName: string;
  isLoading?: boolean;
}

export interface UploadFilesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectAttachments: ProjectAttachment[];
  onFilesUpload: (files: File[]) => void;
  onFileDelete: (fileId: string) => Promise<void>;
  isUploading?: boolean;
  loadingAttachments?: boolean;
  showToast?: (options: { message: string; info?: string; severity: 'error' | 'success' | 'info' }) => void;
  t?: (key: string) => string;
  isWeb?: boolean;
  planDetails?: any;
}

// Allowed file types
const ALLOWED_FILE_TYPES = [
  'text/plain', // txt
  'text/markdown', // md
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
  'application/msword', // doc
  'application/pdf', // pdf
  // PowerPoint files
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // pptx
  'application/vnd.ms-powerpoint', // ppt
  // Excel files
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
  'application/vnd.ms-excel', // xls
    // Images
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
  ];

// Maximum file size (10MB in bytes)
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

// File extension mapping for better error messages
const FILE_TYPE_NAMES: Record<string, string> = {
  'text/plain': 'TXT',
  'text/markdown': 'MD',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/msword': 'DOC',
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
  'application/vnd.ms-powerpoint': 'PPT',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.ms-excel': 'XLS',
    'image/jpeg': 'JPEG',
    'image/jpg': 'JPG',
    'image/png': 'PNG',
    'image/gif': 'GIF',
    'image/webp': 'WEBP',
  };

export function UploadFilesDialog({
  isOpen,
  onClose,
  projectAttachments,
  onFilesUpload,
  onFileDelete,
  isUploading = false,
  loadingAttachments = false,
  showToast,
  t = (key: string) => key,
  isWeb = true,
  planDetails,
}: UploadFilesDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  // Track nested dragenter/dragleave events to prevent flicker
  const [dragDepth, setDragDepth] = useState(0);

  const validateFileTypes = (
    files: FileList
  ): { validFiles: File[]; invalidFiles: File[]; oversizedFiles: File[]; emptyFiles: File[] } => {
    const validFiles: File[] = [];
    const invalidFiles: File[] = [];
    const oversizedFiles: File[] = [];
    const emptyFiles: File[] = [];

    Array.from(files).forEach((file) => {
      // Reject empty files
      if (file.size === 0) {
        emptyFiles.push(file);
        return;
      }

      // Check file size limit
      if (file.size > MAX_FILE_SIZE) {
        oversizedFiles.push(file);
        return;
      }

      // Get file extension
      const fileExtension = file.name.toLowerCase().split('.').pop();

      // Check by MIME type first, then fallback to extension for common cases
      if (
        ALLOWED_FILE_TYPES.includes(file.type) ||
        (fileExtension === 'md' && (file.type === '' || file.type === 'text/plain')) ||
        (fileExtension === 'txt' && file.type === '') ||
        (fileExtension === 'pptx' && file.type === '') ||
        (fileExtension === 'ppt' && file.type === '') ||
        (fileExtension === 'xlsx' && file.type === '') ||
        (fileExtension === 'xls' && file.type === '') ||
        (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension || '') && file.type.startsWith('image/'))
      ) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file);
      }
    });

    return { validFiles, invalidFiles, oversizedFiles, emptyFiles };
  };

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const { validFiles, invalidFiles, oversizedFiles, emptyFiles } = validateFileTypes(files);
      // Show error toast for empty files
      if (emptyFiles.length > 0 && showToast) {
        showToast({
          message: `${emptyFiles.length > 1 ? 'Files' : 'File'} is empty. Please upload non-empty files.`,
          severity: 'error',
        });
      }

      // Show error toast for oversized files
      if (oversizedFiles.length > 0 && showToast) {
        showToast({
          message: `${oversizedFiles.length > 1 ? 'Files' : 'File'} too large. Maximum file size is 10MB.`,
          severity: 'error',
        });
      }

      // Show error toast for invalid files (not supported)
      if (invalidFiles.length > 0 && showToast) {
        const supportedTypes = Object.values(FILE_TYPE_NAMES).join(', ');
        showToast({
          message: `${invalidFiles.length > 1 ? 'Files' : 'File'}  ${
            invalidFiles.length > 1 ? 'are' : 'is'
          } not supported. We currently support: ${supportedTypes}.`,
          severity: 'error',
        });
      }

      // Only allow up to the limit
      let allowedCount = limit - fileCount;
      let filesToUpload = validFiles;
      if (isFreePlan && allowedCount < validFiles.length) {
        filesToUpload = validFiles.slice(0, allowedCount);
        if (showToast) {
          showToast({
            message: `You can only upload ${allowedCount} more file${allowedCount === 1 ? '' : 's'} (limit: ${limit}).`,
            severity: 'info',
          });
        }
      }

      if (filesToUpload.length > 0) {
        try {
          onFilesUpload(filesToUpload);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        } catch (error) {
          console.error('Failed to upload files:', error);
          if (showToast) {
            showToast({
              message: 'Upload failed',
              info: 'Failed to upload files. Please try again.',
              severity: 'error',
            });
          }
        }
      } else {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragOver) setIsDragOver(true);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragDepth((prev) => {
      const next = prev + 1;
      if (next === 1) setIsDragOver(true);
      return next;
    });
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragDepth((prev) => {
      const next = Math.max(prev - 1, 0);
      if (next === 0) setIsDragOver(false);
      return next;
    });
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setDragDepth(0);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await handleFileInputChange({ target: { files } } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    setDeletingFileId(fileId);
    try {
      await onFileDelete(fileId);
    } catch (error) {
      console.error('Failed to delete file:', error);
    } finally {
      setDeletingFileId(null);
    }
  };

  const isFreePlan = planDetails?.name?.toLowerCase() === 'free';
  const fileCount = projectAttachments.length;
  const limit = planDetails?.features?.find((f: any) => f.name === 'Files Per Project')?.maxLimit;
  const atLimit = isFreePlan && typeof limit === 'number' && fileCount >= limit;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent data-testid="upload-files-dialog"   className={`${isWeb ? 'max-w-[85%]' : 'max-w-[50%]'} lg:w-[500px] mx-auto flex flex-col gap-5`}>
        <DialogHeader className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon icon={FilePlus} size={isWeb ? 22 : 18} strokeWidth={2} />
            <DialogTitle>{t('Upload Files')}</DialogTitle>
          </div>
          <button onClick={onClose} className="dialog-close-button">
            <Icon icon={X} color="#667085" strokeWidth={2.5} />
          </button>
        </DialogHeader>
        <p className="dialog-description">
          Files impacting responses. Responses may be lower quality due to the number of files this project is using.
        </p>

        {/* File count and limit info for free plan */}
        {isFreePlan && (
          <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">
            {atLimit
              ? 'You reached the limit, to upload more please remove first'
              : `You are allowed for ${limit} file uploads in free plan`}
          </p>
            <div className={`${atLimit ? 'text-warning-700 bg-warning-50 border-warning-200' : 'text-[#414651] bg-[#FAFAFA] border-[#E9EAEB]'} py-1 px-2 rounded-full text-xs font-medium border`}>
              {`${fileCount} / ${limit}`}
            </div>
          </div>
        )}

        <div>
          <div className="max-h-[60vh] overflow-y-auto">
            {loadingAttachments ? (
              <div className="space-y-2">
                {/* Show 2 skeleton attachments while loading */}
                {[1, 2].map((index) => (
                  <div
                    key={`skeleton-${index}`}
                    className="flex h-[70px] items-center gap-3 p-3 border border-gray-200 rounded-lg bg-white"
                  >
                    <div className="flex-shrink-0">
                      <div className="w-[18px] h-[18px] bg-gray-200 rounded animate-pulse"></div>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col gap-1">
                        <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
                        <div className="h-3 bg-gray-200 rounded animate-pulse w-1/4"></div>
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      <div className="w-[14px] h-[14px] bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : projectAttachments.length === 0 ? (
              <div>
                <span className="text-xs text-gray-500 text-center">Maximum file size: 10 MB</span>
                <button
                  type="button"
                  onClick={handleUploadButtonClick}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`w-full flex flex-col items-center justify-center py-8 px-4 border-2 border-dashed rounded-lg transition-colors focus:outline-none ${
                    isDragOver
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100'
                  }`}
                  disabled={atLimit}
                  style={{ pointerEvents: atLimit ? 'none' : undefined, opacity: atLimit ? 0.6 : 1 }}
                >
                  <div className="mb-3">
                    <Icon icon={FileSearch} size={34} strokeWidth={1.5} color="#252B37" />
                  </div>
                  <span className="text-base font-medium text-gray-700 mb-1">
                    {atLimit
                      ? 'File upload limit reached'
                      : isDragOver
                        ? 'Drop your files here'
                        : t("You haven't added any files yet. Ready to get started?")}
                  </span>
                  <span className="text-sm text-gray-500 text-center">
                    {atLimit
                      ? 'Upgrade to upload more files'
                      : isDragOver
                        ? 'Release to upload'
                        : 'Drag & drop or click to add files'}
                  </span>
                  <span className="text-xs text-gray-400 text-center mt-1">
                   JPG, PNG, PDF, DOC(X), TXT, MD, PPT(X), XLS(X)
                  </span>
                </button>
              </div>
            ) : (
              <div
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`space-y-2 relative ${
                  isDragOver ? 'bg-blue-50 border-2 border-dashed border-blue-400 rounded-lg p-2' : ''
                }`}
              >
                {isDragOver && (
                  <div className="absolute inset-0 flex items-center justify-center bg-blue-50 bg-opacity-90 border-2 border-dashed border-blue-400 rounded-lg z-10 pointer-events-none">
                    <div className="text-center">
                      <Icon icon={FileSearch} size={34} strokeWidth={1.5} color="#3B82F6" className="mx-auto mb-2" />
                      <span className="text-base font-medium text-blue-700">Drop your files here</span>
                      <span className="block text-sm text-blue-600">Release to upload</span>
                    </div>
                  </div>
                )}
                {projectAttachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors"
                  >
                    <div className="relative flex-shrink-0">
                      {!attachment.isLoading && <Icon icon={FileSearch} size={18} strokeWidth={2} color="#029B8C" />}
                      {attachment.isLoading && (
                        <div className="relative">
                          <Icon icon={Paperclip} size={18} strokeWidth={2} color="#F0E5FF" />
                          <div className="absolute top-1 right-1">
                            <div className="w-3 h-3 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col">
                        <div className="text-base font-medium text-gray-900 truncate">
                          {attachment.fileName?.substring(0, attachment.fileName.lastIndexOf('.')) ||
                            attachment.fileName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {attachment.fileName?.substring(attachment.fileName.lastIndexOf('.') + 1)}
                        </div>
                      </div>
                    </div>

                    {!attachment.isLoading && (
                      <button
                        onClick={() => handleDeleteFile(attachment.id)}
                        disabled={deletingFileId === attachment.id || attachment.isLoading}
                        className="flex-shrink-0 p-1 text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                      >
                        {deletingFileId === attachment.id ? (
                          <div className="w-3 h-3 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Icon icon={Trash2} size={14} strokeWidth={1.5} />
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileInputChange}
              className="hidden"
              accept=".txt,.md,.docx,.doc,.pdf,.pptx,.ppt,.xlsx,.xls,.jpeg,.jpg,.png,.gif,.webp,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,image/jpeg,image/jpg,image/png,image/gif,image/webp"
              disabled={atLimit}
            />
          </div>
        </div>
        {/* Upload button */}
        {projectAttachments.length > 0 ? (
          <div className="flex justify-end border-t border-gray-200">
            <Button
              className="mt-5"
              onClick={handleUploadButtonClick}
              disabled={isUploading || atLimit}
              variant="secondary-color"
              size={isWeb ? 'sm' : 'xs'}
              style={{ pointerEvents: atLimit ? 'none' : undefined, opacity: atLimit ? 0.6 : 1 }}
            >
              {atLimit ? 'Upload limit reached' : 'Upload new files'}
            </Button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
